import { useCallback, useState } from 'react'
import { Logo, Menu } from '@highlight/ui'
import { Sparkle } from 'lucide-react'
import { usePaidMentionGate } from '@renderer/chat/components/chat/input/mentions/hooks/use-paid-mention-gate'
import { useModelChoice } from '@renderer/chat/hooks/use-plan-features'
import { useOnSidepanelHidden } from '@renderer/overlay/atoms'
import { useSidebarSearchModel } from './sidebar-search-atoms'
import { useSearchModelChoices, type SearchModelKey } from './sidebar-search-models'

/** Which model the bar's chats go to, shown as its logo. A paid model the plan can't use opens the paywall instead. */
export function SidebarModelMenu() {
  const modelChoice = useModelChoice()
  const [isOpen, setIsOpen] = useState(false)
  useOnSidepanelHidden(useCallback(() => setIsOpen(false), []))
  const [modelKey, setModelKey] = useSidebarSearchModel()
  const choices = useSearchModelChoices()
  const gatePaidMention = usePaidMentionGate()
  const picked = choices.find((choice) => choice.key === modelKey)

  const pickModel = (key: SearchModelKey) => {
    const choice = choices.find((candidate) => candidate.key === key)
    if (choice?.model && gatePaidMention(choice.model, 'sidebar search')) {
      return
    }
    setModelKey(key)
  }

  if (!modelChoice) {
    return null
  }

  return (
    <Menu open={isOpen} onOpenChange={setIsOpen}>
      <Menu.Trigger variant="ghost" size="sm" shape="square" aria-label={`Model: ${picked?.label ?? 'Auto'}`}>
        {picked ? <Logo name={picked.logo} /> : <Sparkle />}
      </Menu.Trigger>
      <Menu.Content align="end" sideOffset={8}>
        <Menu.Group>
          <Menu.GroupLabel>Model</Menu.GroupLabel>
          <Menu.RadioGroup value={modelKey} onValueChange={pickModel}>
            <Menu.RadioItem value="auto" closeOnClick>
              <Sparkle />
              <Menu.ItemText description="Picks a model for each question">Auto</Menu.ItemText>
              <Menu.RadioItemIndicator />
            </Menu.RadioItem>
            {choices.map((choice) => (
              <Menu.RadioItem key={choice.key} value={choice.key} disabled={!choice.model} closeOnClick>
                <Logo name={choice.logo} />
                <Menu.ItemText>{choice.label}</Menu.ItemText>
                <Menu.RadioItemIndicator />
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
        </Menu.Group>
      </Menu.Content>
    </Menu>
  )
}
